<template>
  <modal v-model:show="localShow" :on-close="onClose">
    <div class="whats-new-modal flex flex-col max-h-[80vh]">
      <otello-modal-header
        :header-title="$t('WHATS_NEW.MODAL.TITLE')"
        :header-content="$t('WHATS_NEW.MODAL.SUBTITLE')"
      />

      <div class="flex-1 overflow-y-auto overflow-x-hidden px-6 py-4">
        <div v-if="releases.length > 1" class="release-switcher mb-4">
          <button
            v-for="(release, index) in releases"
            :key="release.version"
            class="release-pill"
            :class="{ 'release-pill--active': index === activeReleaseIndex }"
            @click="activeReleaseIndex = index"
          >
            v{{ release.version }}
          </button>
        </div>

        <section v-if="activeRelease" class="release-card">
          <div class="release-card__meta">
            <span
              class="release-badge"
              :class="releaseTypeClass(activeRelease.type)"
            >
              v{{ activeRelease.version }}
            </span>
            <span class="release-date">{{
              formatDate(activeRelease.date)
            }}</span>
          </div>

          <h3 class="release-title">{{ getTranslatedTitle(activeRelease) }}</h3>

          <p v-if="releaseSummary" class="release-summary">
            {{ releaseSummary }}
          </p>

          <div class="highlights-title">
            {{ $t('WHATS_NEW.MODAL.HIGHLIGHTS_TITLE') }}
          </div>

          <div class="highlights-list">
            <article
              v-for="(item, index) in structuredHighlights"
              :key="`highlight-${item.originalIndex}`"
              class="highlight-card"
            >
              <div class="highlight-index">{{ index + 1 }}</div>

              <div class="highlight-content">
                <h4 class="highlight-heading">{{ item.title }}</h4>
                <p class="highlight-description">{{ item.description }}</p>

                <img
                  v-if="getImageForHighlight(activeRelease, item.originalIndex)"
                  :src="
                    getImageForHighlight(activeRelease, item.originalIndex).url
                  "
                  :alt="
                    getImageForHighlight(activeRelease, item.originalIndex).alt
                  "
                  class="highlight-image"
                />
              </div>
            </article>
          </div>

          <a
            v-if="activeRelease.videoUrl"
            :href="activeRelease.videoUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="video-btn"
          >
            <fluent-icon icon="video" size="16" />
            {{ $t('WHATS_NEW.MODAL.WATCH_VIDEO') }}
          </a>
        </section>
      </div>

      <div class="modal-footer">
        <span v-if="releases.length > 1" class="release-counter">
          {{ activeReleaseIndex + 1 }} / {{ releases.length }}
        </span>
        <otello-button @click="onClose">
          {{ $t('WHATS_NEW.MODAL.GOT_IT') }}
        </otello-button>
      </div>
    </div>
  </modal>
</template>

<script>
import Modal from 'dashboard/components/Modal.vue';
import {
  buildInitialWhatsNewModalState,
  buildStructuredWhatsNewHighlights,
  formatWhatsNewReleaseDate,
  getActiveWhatsNewRelease,
  getWhatsNewImageForHighlight,
  getWhatsNewReleaseSummary,
  parseWhatsNewHighlight,
  resolveWhatsNewReleaseTypeClass,
  resolveWhatsNewTranslatedHighlights,
  resolveWhatsNewTranslatedTitle,
  shouldUseWhatsNewSummary,
} from 'dashboard/helper/whatsNewModal';

export default {
  name: 'WhatsNewModal',
  components: {
    Modal,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    releases: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return buildInitialWhatsNewModalState(this.show);
  },
  computed: {
    activeRelease() {
      return getActiveWhatsNewRelease({
        activeReleaseIndex: this.activeReleaseIndex,
        releases: this.releases,
      });
    },
    translatedHighlights() {
      if (!this.activeRelease) return [];
      return this.getTranslatedHighlights(this.activeRelease);
    },
    hasSummary() {
      return shouldUseWhatsNewSummary(this.translatedHighlights);
    },
    releaseSummary() {
      return getWhatsNewReleaseSummary({
        translatedHighlights: this.translatedHighlights,
      });
    },
    structuredHighlights() {
      return buildStructuredWhatsNewHighlights({
        translatedHighlights: this.translatedHighlights,
      });
    },
  },
  watch: {
    show(val) {
      this.localShow = val;
      if (val) {
        this.activeReleaseIndex = 0;
      }
    },
    localShow(val) {
      if (!val) {
        this.$emit('close');
      }
    },
  },
  methods: {
    onClose() {
      this.localShow = false;
    },
    formatDate(dateString) {
      return formatWhatsNewReleaseDate({
        dateString,
        locale: this.$root.$i18n?.locale || 'it',
      });
    },
    releaseTypeClass(type) {
      return resolveWhatsNewReleaseTypeClass(type);
    },
    getTranslatedTitle(release) {
      return resolveWhatsNewTranslatedTitle({
        release,
        translate: (key) => this.$t(key),
      });
    },
    getTranslatedHighlights(release) {
      return resolveWhatsNewTranslatedHighlights({
        release,
        translate: (key) => this.$t(key),
      });
    },
    getImageForHighlight(release, highlightIndex) {
      return getWhatsNewImageForHighlight({ release, highlightIndex });
    },
    parseHighlight(highlight) {
      return parseWhatsNewHighlight(highlight);
    },
  },
};
</script>

<style scoped>
/* Container */
.whats-new-modal {
  width: min(740px, calc(100vw - 32px));
  max-width: 100%;
}

/* Release switcher */
.release-switcher {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.release-pill {
  border: 1px solid #cbd5e1;
  color: #334155;
  background: #fff;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.release-pill--active {
  border-color: #1b83ff;
  color: #1b83ff;
  background: #eff6ff;
}

.dark .release-pill {
  border-color: #334155;
  color: #cbd5e1;
  background: #1e293b;
}

.dark .release-pill--active {
  border-color: #60a5fa;
  color: #93c5fd;
  background: #1e3a5f;
}

/* Release card */
.release-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  background: #fff;
  max-width: 100%;
}

.dark .release-card {
  border-color: #334155;
  background: #1e293b;
}

.release-card__meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.release-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  letter-spacing: 0.2px;
}

.badge-major {
  background: #eef2ff;
  color: #4338ca;
}

.badge-minor {
  background: #eff6ff;
  color: #1d4ed8;
}

.badge-patch {
  background: #f1f5f9;
  color: #475569;
}

.dark .badge-major {
  background: #312e81;
  color: #c7d2fe;
}

.dark .badge-minor {
  background: #1e3a5f;
  color: #93c5fd;
}

.dark .badge-patch {
  background: #334155;
  color: #cbd5e1;
}

.release-date {
  font-size: 12px;
  color: #94a3b8;
}

.release-title {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 12px;
  letter-spacing: -0.4px;
  line-height: 1.25;
}

.dark .release-title {
  color: #f1f5f9;
}

.release-summary {
  font-size: 14px;
  line-height: 1.6;
  color: #475569;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  margin: 0 0 16px;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.dark .release-summary {
  color: #cbd5e1;
  background: #0f172a;
  border-color: #334155;
}

.highlights-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
  margin: 0 0 10px;
}

.dark .highlights-title {
  color: #94a3b8;
}

/* Highlights */
.highlights-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.highlight-card {
  display: flex;
  gap: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px;
  max-width: 100%;
}

.dark .highlight-card {
  background: #0f172a;
  border-color: #334155;
}

.highlight-index {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: #dbeafe;
  color: #1d4ed8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  margin-top: 1px;
  flex-shrink: 0;
}

.dark .highlight-index {
  background: #1e3a5f;
  color: #93c5fd;
}

.highlight-content {
  min-width: 0;
  width: 100%;
  max-width: 100%;
}

.highlight-heading {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px;
  line-height: 1.3;
}

.dark .highlight-heading {
  color: #f1f5f9;
}

.highlight-description {
  font-size: 13px;
  line-height: 1.55;
  color: #475569;
  margin: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.dark .highlight-description {
  color: #cbd5e1;
}

.highlight-image {
  margin-top: 10px;
  width: 100%;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.dark .highlight-image {
  border-color: #334155;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
}

/* Footer actions */
.video-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  color: white;
  background: #1b83ff;
  border-radius: 8px;
  text-decoration: none;
  transition: background 0.2s;
}

.video-btn:hover {
  background: #1565d8;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
  background: white;
}

.release-counter {
  font-size: 12px;
  color: #64748b;
}

.dark .modal-footer {
  background: #1e293b;
  border-top-color: #334155;
}

.dark .release-counter {
  color: #94a3b8;
}
</style>
